import React from 'react'
import { photoUrl } from '../utils/photoUrl'

// Doctor app settings/menu — matches the Revive Doctor mockups. Self-contained
// full-screen overlay opened from the dashboard hamburger.
const C = {
  blue: '#2563EB', blueSoft: '#EAF1FE', teal: '#14B8A6', navy: '#1E3A5F',
  text: '#1A2233', muted: '#6B7A90', border: '#E8EDF3', bg: '#F6F8FB', card: '#fff', red: '#DC2626',
}
const FONT = "'DM Sans',sans-serif"
const SERIF = "'DM Serif Display',serif"

function Logo() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 20.5S3.5 15.5 2 9.8C1.1 6.4 3.2 4 6 4c1.9 0 3.1 1.1 4 2.2C10.9 5.1 12.1 4 14 4c2.8 0 4.9 2.4 4 5.8C20.5 15.5 12 20.5 12 20.5z" fill={C.teal} />
        <path d="M12 8.2v5M9.5 10.7h5" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
      <div style={{ lineHeight: 1.02 }}>
        <div style={{ fontFamily: SERIF, fontSize: '1rem', color: C.teal, fontWeight: 700 }}>Revive</div>
        <div style={{ fontSize: '.68rem', color: C.navy, fontWeight: 800 }}>Doctor</div>
      </div>
    </div>
  )
}

function TopBar({ onBack, onClose }) {
  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff', display: 'flex', alignItems: 'center', gap: 12, padding: '.7rem 1rem', borderBottom: `1px solid ${C.border}` }}>
      <button onClick={onBack || onClose} aria-label="Back"
        style={{ width: 38, height: 38, borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', cursor: 'pointer', fontSize: '1.1rem' }}>{onBack ? '‹' : '✕'}</button>
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}><Logo /></div>
      <div style={{ width: 38 }} />
    </div>
  )
}

const Row = ({ icon, bg, label, sub, onClick, danger }) => (
  <button onClick={onClick} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '.9rem 1rem', background: 'none', border: 'none', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', fontFamily: FONT, textAlign: 'left' }}>
    <div style={{ width: 38, height: 38, borderRadius: 11, background: bg || C.blueSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.05rem', flexShrink: 0 }}>{icon}</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '.9rem', fontWeight: 700, color: danger ? C.red : C.text }}>{label}</div>
      {sub && <div style={{ fontSize: '.72rem', color: C.muted }}>{sub}</div>}
    </div>
    {!danger && <span style={{ color: C.muted }}>›</span>}
  </button>
)

const Field = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '.7rem 0', borderBottom: `1px solid ${C.border}` }}>
    <span style={{ fontSize: '.78rem', color: C.muted }}>{label}</span>
    <span style={{ fontSize: '.82rem', color: C.text, fontWeight: 600, textAlign: 'right' }}>{value || '—'}</span>
  </div>
)

const Toggle = ({ on, onChange }) => (
  <button onClick={() => onChange(!on)} aria-label="toggle" style={{ width: 44, height: 26, borderRadius: 999, border: 'none', background: on ? C.blue : '#CBD5E1', position: 'relative', cursor: 'pointer', flexShrink: 0, transition: 'background .15s' }}>
    <span style={{ position: 'absolute', top: 3, left: on ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
  </button>
)

const Labeled = ({ label, children }) => (
  <div style={{ marginBottom: '.9rem' }}>
    <label style={{ fontSize: '.72rem', fontWeight: 600, color: C.muted }}>{label}</label>
    {children}
  </div>
)
const inputStyle = { width: '100%', marginTop: 4, padding: '.7rem .9rem', border: `1.5px solid ${C.border}`, borderRadius: 10, fontSize: '.88rem', outline: 'none', fontFamily: FONT, boxSizing: 'border-box', color: C.text }

export default function DoctorSettings({ user, apiBase, fetchWithAuth, updateUser, logout, onClose }) {
  const [view, setView] = React.useState('menu')
  const [profile, setProfile] = React.useState(null)
  const [signOutOpen, setSignOutOpen] = React.useState(false)
  const [edit, setEdit] = React.useState(null)  // profile edit form
  const [saving, setSaving] = React.useState(false)
  const [msg, setMsg] = React.useState('')
  const [notif, setNotif] = React.useState(null)          // notification prefs
  const [clinic, setClinic] = React.useState(null)        // clinic settings
  const [hasPw, setHasPw] = React.useState(false)
  const [pw, setPw] = React.useState({ current: '', next: '', confirm: '' })
  const photoRef = React.useRef(null)
  const [uploadingPhoto, setUploadingPhoto] = React.useState(false)
  const uploadPhoto = async (files) => {
    const f = files && files[0]; if (!f) return
    setUploadingPhoto(true)
    try {
      const fd = new FormData(); fd.append('photo', f)
      const r = await fetchWithAuth(`${apiBase}/api/doctor/photo`, { method: 'POST', body: fd })
      const d = await r.json()
      if (r.ok && d.photo) setProfile(p => ({ ...(p || {}), photo: d.photo }))
    } catch (e) {} finally { setUploadingPhoto(false) }
  }

  // Load the data for the backend-backed screens when opened
  React.useEffect(() => {
    if (view === 'notifications' && !notif) fetchWithAuth(`${apiBase}/api/doctor/notification-prefs`).then(r => r.json()).then(d => setNotif(d.prefs || {})).catch(() => setNotif({}))
    if (view === 'clinic' && !clinic) fetchWithAuth(`${apiBase}/api/doctor/clinic`).then(r => r.json()).then(d => setClinic(d.clinic || {})).catch(() => setClinic({}))
    if (view === 'security') fetchWithAuth(`${apiBase}/api/doctor/has-password`).then(r => r.json()).then(d => setHasPw(!!d.hasPassword)).catch(() => {})
  }, [view]) // eslint-disable-line react-hooks/exhaustive-deps

  const saveNotif = (next) => {
    setNotif(next)
    fetchWithAuth(`${apiBase}/api/doctor/notification-prefs`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ prefs: next }) }).catch(() => {})
  }
  const saveClinic = async () => {
    setSaving(true); setMsg('')
    try {
      const r = await fetchWithAuth(`${apiBase}/api/doctor/clinic`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(clinic) })
      if (!r.ok) throw new Error('Could not save')
      setMsg('Saved ✓'); setTimeout(() => setMsg(''), 1500)
    } catch (e) { setMsg(e.message) } finally { setSaving(false) }
  }
  const changePassword = async () => {
    setMsg('')
    if (pw.next.length < 6) { setMsg('New password must be at least 6 characters.'); return }
    if (pw.next !== pw.confirm) { setMsg('New passwords do not match.'); return }
    setSaving(true)
    try {
      const r = await fetchWithAuth(`${apiBase}/api/doctor/set-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ currentPassword: pw.current, newPassword: pw.next }) })
      const d = await r.json()
      if (!r.ok) throw new Error(d.message || 'Could not update')
      setHasPw(true); setPw({ current: '', next: '', confirm: '' }); setMsg('Password updated ✓')
    } catch (e) { setMsg(e.message) } finally { setSaving(false) }
  }

  React.useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const r = await fetchWithAuth(`${apiBase}/api/doctor/profile`)
        const d = await r.json()
        if (alive && r.ok) setProfile(d.profile || null)
      } catch (e) {}
    })()
    return () => { alive = false }
  }, [apiBase, fetchWithAuth])

  const name = profile?.name || user?.name || 'Doctor'
  const initial = (name[0] || 'D').toUpperCase()
  const spec = profile?.specialization || 'Dermatology'

  const saveProfile = async () => {
    setSaving(true); setMsg('')
    try {
      const r = await fetchWithAuth(`${apiBase}/api/doctor/profile`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: edit.name.trim(), email: edit.email.trim(), phone: (edit.phone || '').trim() }),
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.message || 'Could not save')
      setProfile(p => ({ ...p, ...d.profile })); updateUser && updateUser({ name: d.profile.name, email: d.profile.email })
      setMsg(d.emailChanged ? 'Saved — log out and back in for the email change to fully apply.' : 'Saved ✓')
      if (!d.emailChanged) setTimeout(() => setEdit(null), 800)
    } catch (e) { setMsg(e.message) }
    finally { setSaving(false) }
  }

  const wrap = { position: 'fixed', inset: 0, zIndex: 3000, background: C.bg, fontFamily: FONT, overflowY: 'auto' }
  const avatar = (size = 44) => (
    profile?.photo
      ? <img src={photoUrl(profile.photo)} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: '#eee' }} />
      : <div style={{ width: size, height: size, borderRadius: '50%', background: `linear-gradient(135deg,${C.blue},#60A5FA)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: SERIF, fontSize: size * 0.42, flexShrink: 0 }}>{initial}</div>
  )

  // ── MENU ────────────────────────────────────────────────────────────────────
  if (view === 'menu') return (
    <div style={wrap}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '.9rem 1rem', background: '#fff', borderBottom: `1px solid ${C.border}` }}>
        <button onClick={onClose} aria-label="Close" style={{ width: 38, height: 38, borderRadius: 10, border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.2rem' }}>✕</button>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}><Logo /></div>
        <div style={{ width: 38 }} />
      </div>

      <div style={{ padding: '1rem' }}>
        {/* profile card */}
        <button onClick={() => setView('profile')} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, padding: '1rem', cursor: 'pointer', fontFamily: FONT, textAlign: 'left', marginBottom: '1rem' }}>
          {avatar(52)}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '1rem', fontWeight: 800, color: C.text }}>Dr. {name}</div>
            <div style={{ fontSize: '.76rem', color: C.muted }}>{spec}</div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, background: C.blueSoft, color: C.blue, fontSize: '.66rem', fontWeight: 700, padding: '.12rem .5rem', borderRadius: 999 }}>🛡️ Verified Doctor</div>
          </div>
        </button>

        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden' }}>
          <Row icon="👤" bg={C.blueSoft} label="Profile" onClick={() => setView('profile')} />
          <Row icon="🏥" bg="#E7F7EE" label="Clinic Settings" onClick={() => setView('clinic')} />
          <Row icon="🔔" bg="#F1EBFE" label="Notification Settings" onClick={() => setView('notifications')} />
          <Row icon="🛡️" bg={C.blueSoft} label="Security" onClick={() => setView('security')} />
          <Row icon="❓" bg="#F0F2F5" label="Help & Support" onClick={() => setView('help')} />
          <Row icon="🔒" bg="#F1EBFE" label="Privacy Policy" onClick={() => setView('privacy')} />
          <Row icon="📄" bg="#FEF3E2" label="Terms & Conditions" onClick={() => setView('terms')} />
        </div>

        <button onClick={() => setSignOutOpen(true)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, marginTop: '1rem', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, padding: '.9rem 1rem', cursor: 'pointer', fontFamily: FONT }}>
          <div style={{ width: 38, height: 38, borderRadius: 11, background: '#FEECEC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.05rem' }}>↩</div>
          <div style={{ fontSize: '.9rem', fontWeight: 700, color: C.red }}>Sign Out</div>
        </button>

        <div style={{ textAlign: 'center', color: C.muted, fontSize: '.7rem', marginTop: '1.5rem' }}>App Version 2.1.0</div>
      </div>

      {signOutOpen && (
        <div onClick={() => setSignOutOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 3100, background: 'rgba(10,20,15,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 20, padding: '1.75rem', width: '100%', maxWidth: 340, textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, margin: '0 auto .8rem', borderRadius: '50%', border: `2px solid ${C.red}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.red, fontSize: '1.4rem' }}>↩</div>
            <div style={{ fontFamily: SERIF, fontSize: '1.2rem', color: C.text }}>Sign Out?</div>
            <div style={{ fontSize: '.82rem', color: C.muted, margin: '.4rem 0 1.2rem' }}>Are you sure you want to sign out of your account?</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setSignOutOpen(false)} style={{ flex: 1, padding: '.8rem', borderRadius: 11, background: '#fff', border: `1px solid ${C.border}`, color: C.muted, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>Cancel</button>
              <button onClick={() => { setSignOutOpen(false); onClose && onClose(); logout && logout() }} style={{ flex: 1, padding: '.8rem', borderRadius: 11, background: C.red, border: 'none', color: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>Sign Out</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  // ── PROFILE ─────────────────────────────────────────────────────────────────
  if (view === 'profile') return (
    <div style={wrap}>
      <TopBar onBack={() => { setView('menu'); setEdit(null) }} />
      <div style={{ padding: '1rem' }}>
        <div style={{ fontFamily: SERIF, fontSize: '1.5rem', color: C.text, marginBottom: '.8rem' }}>Profile</div>
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, padding: '1.2rem', textAlign: 'center', marginBottom: '1rem', position: 'relative' }}>
          <button onClick={() => setEdit({ name, email: profile?.email || user?.email || '', phone: profile?.phone || '' })} style={{ position: 'absolute', top: 12, right: 12, width: 34, height: 34, borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', cursor: 'pointer' }}>✏️</button>
          <div style={{ position: 'relative', width: 76, margin: '0 auto' }}>
            {avatar(76)}
            <button onClick={() => photoRef.current && photoRef.current.click()} disabled={uploadingPhoto} aria-label="Change photo"
              style={{ position: 'absolute', bottom: -2, right: -2, width: 28, height: 28, borderRadius: '50%', background: C.blue, border: '2px solid #fff', color: '#fff', fontSize: '.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{uploadingPhoto ? '…' : '📷'}</button>
            <input ref={photoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { uploadPhoto(e.target.files); e.target.value = '' }} />
          </div>
          <div style={{ fontSize: '1.15rem', fontWeight: 800, color: C.text, marginTop: '.6rem' }}>Dr. {name}</div>
          <div style={{ fontSize: '.8rem', color: C.muted }}>{spec}</div>
          <div style={{ fontSize: '.76rem', color: C.muted }}>{profile?.qualification || ''}</div>
        </div>

        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, padding: '.4rem 1rem 1rem', marginBottom: '1rem' }}>
          <div style={{ fontSize: '.72rem', fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: '.05em', padding: '.9rem 0 .3rem' }}>Personal Information</div>
          <Field label="Full Name" value={`Dr. ${String(name || '').replace(/^\s*dr\.?\s+/i, '').trim()}`} />
          <Field label="Email" value={profile?.email || user?.email} />
          <Field label="Phone" value={profile?.phone} />
        </div>

        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, padding: '.4rem 1rem 1rem' }}>
          <div style={{ fontSize: '.72rem', fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: '.05em', padding: '.9rem 0 .3rem' }}>Professional Information</div>
          <Field label="Specialization" value={profile?.specialization} />
          <Field label="Experience" value={profile?.experience ? `${profile.experience} Years` : ''} />
          <Field label="Qualification" value={profile?.qualification} />
        </div>
      </div>

      {edit && (
        <div onClick={() => setEdit(null)} style={{ position: 'fixed', inset: 0, zIndex: 3100, background: 'rgba(10,20,15,.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '1.5rem', width: '100%', maxWidth: 480 }}>
            <div style={{ fontFamily: SERIF, fontSize: '1.2rem', color: C.text, marginBottom: '1rem' }}>Edit profile</div>
            {['name', 'email', 'phone'].map(k => (
              <div key={k} style={{ marginBottom: '.8rem' }}>
                <label style={{ fontSize: '.72rem', fontWeight: 600, color: C.muted, textTransform: 'capitalize' }}>{k}</label>
                <input value={edit[k]} onChange={e => setEdit(s => ({ ...s, [k]: e.target.value }))}
                  style={{ width: '100%', marginTop: 4, padding: '.7rem .9rem', border: `1.5px solid ${C.border}`, borderRadius: 10, fontSize: '.9rem', outline: 'none', fontFamily: FONT, boxSizing: 'border-box' }} />
              </div>
            ))}
            {msg && <div style={{ fontSize: '.78rem', color: msg.includes('✓') || msg.includes('Saved') ? C.teal : C.red, marginBottom: '.6rem' }}>{msg}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setEdit(null)} style={{ flex: 1, padding: '.8rem', borderRadius: 11, background: '#fff', border: `1px solid ${C.border}`, color: C.muted, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>Cancel</button>
              <button onClick={saveProfile} disabled={saving} style={{ flex: 2, padding: '.8rem', borderRadius: 11, background: C.blue, border: 'none', color: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>{saving ? 'Saving…' : 'Save changes'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  // ── HELP & SUPPORT ──────────────────────────────────────────────────────────
  if (view === 'help') return (
    <div style={wrap}>
      <TopBar onBack={() => setView('menu')} />
      <div style={{ padding: '1rem' }}>
        <div style={{ fontFamily: SERIF, fontSize: '1.5rem', color: C.text }}>Help & Support</div>
        <div style={{ fontSize: '.8rem', color: C.muted, marginBottom: '1rem' }}>We are here to help you</div>
        <div style={{ fontSize: '.82rem', fontWeight: 800, color: C.text, margin: '.5rem 0' }}>Frequently Asked Questions</div>
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden', marginBottom: '1.2rem' }}>
          {['How to add a new patient?', 'How to manage appointments?', 'How to view patient progress photos?', 'How do alerts work?'].map(q => (
            <Row key={q} icon="›" bg="#F0F2F5" label={q} onClick={() => {}} />
          ))}
        </div>
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden' }}>
          <Row icon="💬" bg={C.blueSoft} label="Contact Support" sub="Chat with our support team" onClick={() => {}} />
          <Row icon="📞" bg="#E7F7EE" label="Call Us" sub="+91 40 1234 5678" onClick={() => { window.location.href = 'tel:+914012345678' }} />
          <Row icon="📧" bg="#FEF3E2" label="Email Us" sub="support@revivehc.in" onClick={() => { window.location.href = 'mailto:support@revivehc.in' }} />
        </div>
      </div>
    </div>
  )

  // ── PRIVACY / TERMS (static legal) ──────────────────────────────────────────
  if (view === 'privacy' || view === 'terms') return (
    <div style={wrap}>
      <TopBar onBack={() => setView('menu')} />
      <div style={{ padding: '1rem' }}>
        <div style={{ fontFamily: SERIF, fontSize: '1.5rem', color: C.text }}>{view === 'terms' ? 'Terms & Conditions' : 'Privacy Policy'}</div>
        <div style={{ fontSize: '.72rem', color: C.muted, marginBottom: '1rem' }}>Last updated: 1 May 2026</div>
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, padding: '1.1rem', fontSize: '.84rem', color: C.text, lineHeight: 1.7 }}>
          <p>Welcome to Revive Doctor. {view === 'terms' ? 'These terms govern your use of the app.' : 'This policy explains how we handle your data.'}</p>
          <p><b>1. {view === 'terms' ? 'Acceptance of terms' : 'Data we collect'}</b><br />By using the app you agree to use it lawfully and in line with your clinic's obligations.</p>
          <p><b>2. Use of the app</b><br />The app is for approved doctors to manage patients, appointments, and clinical notes.</p>
          <p><b>3. Privacy</b><br />Patient data is handled per applicable regulations; do not share access.</p>
          <p><b>4. Contact</b><br />Questions? Email <a href="mailto:support@revivehc.in" style={{ color: C.blue }}>support@revivehc.in</a>.</p>
          <p style={{ marginTop: '1rem' }}>
            <a href="/terms-of-service" style={{ color: C.blue }}>Full Terms →</a>{'  '}
            <a href="/privacy-policy" style={{ color: C.blue, marginLeft: 12 }}>Full Privacy Policy →</a>
          </p>
        </div>
      </div>
    </div>
  )

  // ── NOTIFICATION SETTINGS ───────────────────────────────────────────────────
  if (view === 'notifications') {
    const n = notif || {}
    const set = (k, v) => saveNotif({ ...n, [k]: v })
    const on = k => n[k] !== false // default on
    return (
      <div style={wrap}>
        <TopBar onBack={() => setView('menu')} />
        <div style={{ padding: '1rem' }}>
          <div style={{ fontFamily: SERIF, fontSize: '1.5rem', color: C.text }}>Notification Settings</div>
          <div style={{ fontSize: '.8rem', color: C.muted, marginBottom: '1rem' }}>Manage your notification preferences</div>

          <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.9rem 1rem', borderBottom: `1px solid ${C.border}`, background: C.bg }}>
              <div style={{ fontSize: '.9rem', fontWeight: 800, color: C.text }}>Push Notifications</div>
              <Toggle on={on('push')} onChange={v => set('push', v)} />
            </div>
            {[
              ['📅', C.blueSoft, 'Appointments', 'Get notified about new appointments', 'appointments'],
              ['💬', '#F1EBFE', 'New Messages', 'Get notified about new messages', 'messages'],
              ['🔔', '#FEF3E2', 'Alerts', 'Get notified about important alerts', 'alerts'],
              ['👥', '#E7F7EE', 'Follow-ups', 'Get notified about patient follow-ups', 'followups'],
              ['⚙️', '#F0F2F5', 'System Updates', 'Get notified about system updates', 'systemUpdates'],
            ].map(([ic, bg, label, sub, k]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '.85rem 1rem', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>{ic}</div>
                <div style={{ flex: 1 }}><div style={{ fontSize: '.86rem', fontWeight: 700, color: C.text }}>{label}</div><div style={{ fontSize: '.7rem', color: C.muted }}>{sub}</div></div>
                <Toggle on={on(k)} onChange={v => set(k, v)} />
              </div>
            ))}
          </div>

          <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.9rem 1rem', borderBottom: `1px solid ${C.border}`, background: C.bg }}>
              <div style={{ fontSize: '.9rem', fontWeight: 800, color: C.text }}>Email Notifications</div>
              <Toggle on={on('email')} onChange={v => set('email', v)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '.85rem 1rem' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: C.blueSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>📧</div>
              <div style={{ flex: 1 }}><div style={{ fontSize: '.86rem', fontWeight: 700, color: C.text }}>Email Summary</div><div style={{ fontSize: '.7rem', color: C.muted }}>Receive a daily email summary</div></div>
              <Toggle on={on('emailSummary')} onChange={v => set('emailSummary', v)} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ── CLINIC SETTINGS ─────────────────────────────────────────────────────────
  if (view === 'clinic') {
    const c = clinic || {}
    const setC = (k, v) => setClinic({ ...c, [k]: v })
    return (
      <div style={wrap}>
        <TopBar onBack={() => { setView('menu'); setMsg('') }} />
        <div style={{ padding: '1rem' }}>
          <div style={{ fontFamily: SERIF, fontSize: '1.5rem', color: C.text }}>Clinic Settings</div>
          <div style={{ fontSize: '.8rem', color: C.muted, marginBottom: '1rem' }}>Manage your clinic details and preferences</div>
          {clinic === null ? <div style={{ color: C.muted, fontSize: '.85rem' }}>Loading…</div> : (
            <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, padding: '1rem' }}>
              <Labeled label="Clinic Name"><input value={c.clinic_name || ''} onChange={e => setC('clinic_name', e.target.value)} style={inputStyle} placeholder="Revive Skin Clinic" /></Labeled>
              <Labeled label="Clinic Address"><textarea value={c.clinic_address || ''} onChange={e => setC('clinic_address', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Address" /></Labeled>
              <Labeled label="Consultation Type"><input value={c.consultation_type || ''} onChange={e => setC('consultation_type', e.target.value)} style={inputStyle} placeholder="In-clinic & Online" /></Labeled>
              <Labeled label="Consultation Fee (₹)"><input value={c.consultation_fee || ''} onChange={e => setC('consultation_fee', e.target.value)} inputMode="numeric" style={inputStyle} placeholder="800" /></Labeled>
              <Labeled label="Clinic Phone"><input value={c.clinic_phone || ''} onChange={e => setC('clinic_phone', e.target.value)} type="tel" style={inputStyle} placeholder="+91 40 1234 5678" /></Labeled>
              <Labeled label="Working Hours"><input value={c.working_hours || ''} onChange={e => setC('working_hours', e.target.value)} style={inputStyle} placeholder="Mon–Sat: 9:00 AM – 7:00 PM" /></Labeled>
              <Labeled label="About Clinic"><textarea value={c.about_clinic || ''} onChange={e => setC('about_clinic', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="We provide expert dermatology care…" /></Labeled>
              {msg && <div style={{ fontSize: '.8rem', color: msg.includes('✓') ? C.teal : C.red, marginBottom: '.6rem' }}>{msg}</div>}
              <button onClick={saveClinic} disabled={saving} style={{ width: '100%', padding: '.85rem', borderRadius: 11, background: C.blue, border: 'none', color: '#fff', fontWeight: 800, fontSize: '.9rem', cursor: 'pointer', fontFamily: FONT }}>{saving ? 'Saving…' : 'Save Clinic Settings'}</button>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── SECURITY (change password) ──────────────────────────────────────────────
  if (view === 'security') return (
    <div style={wrap}>
      <TopBar onBack={() => { setView('menu'); setMsg(''); setPw({ current: '', next: '', confirm: '' }) }} />
      <div style={{ padding: '1rem' }}>
        <div style={{ fontFamily: SERIF, fontSize: '1.5rem', color: C.text }}>Security</div>
        <div style={{ fontSize: '.8rem', color: C.muted, marginBottom: '1rem' }}>Manage your account security</div>
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, padding: '1rem', marginBottom: '1rem' }}>
          <div style={{ fontSize: '.9rem', fontWeight: 800, color: C.text, marginBottom: '.2rem' }}>{hasPw ? 'Change Password' : 'Set a Password'}</div>
          <div style={{ fontSize: '.72rem', color: C.muted, marginBottom: '.9rem' }}>You sign in with an OTP; a password lets you log in without WhatsApp.</div>
          {hasPw && <Labeled label="Current Password"><input type="password" value={pw.current} onChange={e => setPw(s => ({ ...s, current: e.target.value }))} style={inputStyle} /></Labeled>}
          <Labeled label="New Password"><input type="password" value={pw.next} onChange={e => setPw(s => ({ ...s, next: e.target.value }))} style={inputStyle} placeholder="At least 6 characters" /></Labeled>
          <Labeled label="Confirm New Password"><input type="password" value={pw.confirm} onChange={e => setPw(s => ({ ...s, confirm: e.target.value }))} style={inputStyle} /></Labeled>
          {msg && <div style={{ fontSize: '.8rem', color: msg.includes('✓') ? C.teal : C.red, marginBottom: '.6rem' }}>{msg}</div>}
          <button onClick={changePassword} disabled={saving} style={{ width: '100%', padding: '.85rem', borderRadius: 11, background: C.blue, border: 'none', color: '#fff', fontWeight: 800, fontSize: '.9rem', cursor: 'pointer', fontFamily: FONT }}>{saving ? 'Saving…' : hasPw ? 'Update Password' : 'Set Password'}</button>
        </div>
        <button onClick={() => { onClose && onClose(); logout && logout() }} style={{ width: '100%', padding: '.85rem', borderRadius: 12, background: '#fff', border: `1px solid ${C.border}`, color: C.red, fontWeight: 700, fontSize: '.86rem', cursor: 'pointer', fontFamily: FONT }}>Log out of this device</button>
      </div>
    </div>
  )

  return null
}
